import type { CSSProperties } from 'react'
import type React from 'react'
import { canWriteEntity } from '@tunasong/models'
import { useCurrentUser } from '@tunasong/redux'
import type { Entity } from '@tunasong/schemas'
import { entityTypeNames, isEntity, isPersisted } from '@tunasong/schemas'
import { useState } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import invariant from 'tiny-invariant'
import type { EntityListItemProps } from '../entity/entity-list-item.js'
import { EntityListItem } from '../entity/entity-list-item.js'
import { moveCaption, refuseCaption } from '../entity/container-drop.js'
import { useMergeRefs } from '../hooks/merge-refs.js'
import type { DropTarget } from './constants.js'
import DndDivider from './dnd-divider.js'

interface DraggableProps extends EntityListItemProps {
  element: Entity
  size?: 'small' | 'tiny' | 'large' | 'medium'
  /** Ref for keyboard navigation scroll-into-view */
  navRef?: React.Ref<HTMLLIElement>

  /** -1 is top,  */
  onDropped?(source: Entity, target: DropTarget): void
  onHovering?(source: Entity, target: Entity): void
  isDropTarget(params: { source?: Entity; target: Entity }): boolean
  /** The row is a container: a drop it won't take is refused, never a re-order (GH #3164) */
  isContainer?(target: Entity): boolean
}

export const DraggableListItem = ({
  element,
  isDropTarget,
  isContainer = () => false,
  onHovering,
  onDropped,
  navRef,
  ...restProps
}: DraggableProps) => {
  const { userId } = useCurrentUser()
  /** An unsaved entity has no owner yet and can't be someone else's; a saved one needs write access */
  const canWrite = (entity: Entity) => !isPersisted(entity) || canWriteEntity(userId, entity)
  /** An item the user can't write to can't be moved, so it can't be dragged (GH #2450) */
  const canMove = canWrite(element)
  const [{ opacity }, dragRef, preview] = useDrag(
    () => ({
      type: element.type,
      item: element,
      canDrag: () => canMove,
      collect: monitor => ({
        opacity: monitor.isDragging() ? 0.5 : 1,
      }),
    }),
    [element, canMove]
  )

  /**
   * Dropping onto a target (a folder) moves the item into it: both must be writable. A target the user can't write
   * to stays in place, since hiding it would reshuffle the list mid-drag, but takes no drop (GH #2450). A re-order
   * drop is unchanged.
   */
  const canDrop = (item: Entity) => {
    if (item?.id === element?.id) {
      return false
    }
    if (!isDropTarget({ source: item, target: element })) {
      /** A container that won't take the type refuses; any other row is a re-order target */
      return !isContainer(element)
    }
    return canWrite(item) && canWrite(element)
  }
  /** A container that won't take the dragged item: by type, or by write access (GH #2450, #3164) */
  const refuses = (dragged: Entity) =>
    isDropTarget({ source: dragged, target: element }) ? !canDrop(dragged) : isContainer(element)
  /** While something is dragged, a target that can't take it says so, for keyboard and screen readers (GH #2450) */
  const isRefusing = (dragged: Entity | null) =>
    Boolean(dragged) && dragged?.id !== element.id && refuses(dragged as Entity)

  /** Send exactly one hover messsage per source, target pair */
  const [lastHover, setLastHover] = useState<{ source: Entity; target: Entity } | null>(null)
  const [{ isHovering, isDropAllowed, isDropRefused }, dropRef] = useDrop({
    accept: entityTypeNames as never,
    drop: item => {
      if (onDropped) {
        onDropped(item, element)
      }
    },
    hover: item => {
      if (item === lastHover?.source && element === lastHover?.target) {
        return
      }
      invariant(isEntity(item), 'Item must be an Entity')
      setLastHover({ source: item, target: element })
      if (onHovering) {
        onHovering(item, element)
      }
    },
    canDrop,
    collect: monitor => ({
      isHovering: monitor.isOver({ shallow: true }), // canDrop(monitor.getItem()) &&,
      isDropAllowed: monitor.canDrop(),
      isDropRefused: isRefusing(monitor.getItem<Entity | null>()),
    }),
  })

  const mergedRef = useMergeRefs<HTMLLIElement>(dragRef as never, dropRef as never, preview as never, navRef as never)

  /** Kind B on the hovered row (GH #3164 §3): it takes the drop, or it is a container that won't */
  const isAccepting = isHovering && isDropAllowed && lastHover !== null && isDropTarget(lastHover)
  const isRefusingHere = isHovering && isDropRefused

  /* The dragged row only dims: a fill there would outshout the folder the user is aiming at */
  const entityListItemStyle: CSSProperties = {
    /* A drop target, e.g. a folder: an inset outline and a tint, so the row keeps its size and nothing below jumps */
    ...(isAccepting && {
      outlineWidth: 2,
      outlineStyle: 'solid',
      outlineColor: 'var(--tuna-palette-primary-main)',
      outlineOffset: -2,
      backgroundColor: 'var(--tuna-palette-action-selected)',
      borderRadius: 4,
    }),
    /* A container that won't take it: a dashed muted outline and the not-allowed cursor, the same size */
    ...(isRefusingHere && {
      outlineWidth: 2,
      outlineStyle: 'dashed',
      outlineColor: 'var(--tuna-palette-divider)',
      outlineOffset: -2,
      borderRadius: 4,
      cursor: 'not-allowed',
    }),
    opacity,
  }
  const dropCaption = isAccepting ? moveCaption(element) : isRefusingHere ? refuseCaption(element) : undefined

  /**
   * @see https://react-dnd.github.io/react-dnd/examples/sortable/simple
   *
   * We have an issue where the drag preview is showing not just the item being dragged, but the entire list.
   * @see https://stackoverflow.com/questions/42234575/react-dnd-showing-entire-list-when-dragging
   *
   */

  return (
    <>
      {/* If we are hovering and we are not a drop target (e.g., folder), show a re-order divider */}
      {isHovering && lastHover && !isDropTarget(lastHover) && !isContainer(element) ? (
        <li aria-hidden="true">
          <DndDivider />
        </li>
      ) : null}
      <EntityListItem
        {...restProps}
        style={entityListItemStyle}
        element={element}
        ref={mergedRef}
        aria-disabled={isDropRefused ? 'true' : undefined}
        dropCaption={dropCaption}
      />
    </>
  )
}
